import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';
import KuusiKenttaaFields, { Intro, Err } from './KuusiKenttaaFields';

/**
 * KuusiKenttaaTask — the /me task "Kuusi kenttää" (kuusi_kenttaa.v1,
 * specs/workshop-modules/kuusi_kenttaa.md): the signed-in participant's door
 * to the six fields, through the `/me` routes (KUUSI_KENTTAA_ME). The UI itself is
 * KuusiKenttaaFields, shared with the room page (KuusiKenttaaRoomScreen).
 *
 * The project is chosen once per device (?hanke=<slug> in the URL) and
 * remembered server-side by the answers themselves. Phone width first.
 * A saved answer brings the AI's follow-up question: the view is fetched again
 * after a save and every few seconds while a question is still being asked.
 */

async function send(url, method, body) {
  const r = await fetch(url, {
    method, credentials: 'include', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}

/** While the AI is still asking a follow-up, the view is fetched again every few seconds. */
export const POLL_MS = 4000;
export const hasPending = (data) => Boolean(data?.items?.some((i) => i.followup?.status === 'pending'));

export default function KuusiKenttaaTask({ instanceId, locked = false, onChange = null }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = searchParams.get('hanke') || null;
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const [tick, setTick] = useState(0); // bumped to fetch again: after a save, and while a follow-up is pending
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const qs = `?instanceId=${encodeURIComponent(instanceId)}${project ? `&project=${encodeURIComponent(project)}` : ''}`;
    fetch(API_ENDPOINTS.KUUSI_KENTTAA_ME() + qs, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`); return d; })
      .then((d) => { if (!cancelled) { setState({ loading: false, error: null, data: d }); onChange?.(d); } })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message, data: null }); });
    return () => { cancelled = true; };
  }, [instanceId, project, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasPending(state.data)) return undefined;
    const id = setTimeout(reload, POLL_MS);
    return () => clearTimeout(id);
  }, [state.data, reload]);

  const save = useCallback((body) => send(API_ENDPOINTS.KUUSI_KENTTAA_ME(), 'PUT', { instanceId, ...body }).then((d) => d.answer), [instanceId]);
  const reply = useCallback((body) => send(API_ENDPOINTS.KUUSI_KENTTAA_ME_FOLLOWUP(), 'POST', { instanceId, ...body }).then((d) => { reload(); return d.followup; }), [instanceId, reload]);
  const pick = (slug) => { const p = new URLSearchParams(searchParams); p.set('hanke', slug); setSearchParams(p); };
  const unpick = () => { const p = new URLSearchParams(searchParams); p.delete('hanke'); setSearchParams(p); };

  if (state.loading) return <Intro>{t('kuusiKenttaa.loading')}</Intro>;
  if (state.error) return <Err>{t('kuusiKenttaa.loadError', { message: state.error })}</Err>;
  if (!state.data?.enabled) return null;

  return <KuusiKenttaaFields data={state.data} locked={locked} onPick={pick} onUnpick={unpick} putAnswer={save} putFollowup={reply} onSaved={reload} />;
}
